import * as React from "react";
import { createContext, ReactNode, useCallback, useEffect, useState } from "react";
import { eLynxAppIDEnum } from "../../common/enums/eLynxAppIDEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useBreadcrumb } from "../../queries/breadcrumb/UseBreadcrumb";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { routesByViewID, UIViewCategory } from "../../routes/routes";
import { IUIUserFavoriteModel, IUIUserFavoriteRouteDetailModel } from "../../services/ui-personalization-data-service/Models";
import { useOfflineDetectContext } from "../offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../user-context/UserContext";
import { useSaveUserFavorite } from "../../queries/user-favorites/UseSaveUserFavorite";
import { splitFavoriteDescription } from "../../common/utilities/SplitFavoriteDescription";
import { useConfirmation } from "../confirmation-context/ConfirmationContext";
import { Box, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEvaluateFeature } from "../../queries/features/UseEvaluateFeature";
import { FeatureConstants } from "../../services/features-data-service/Models";
import { subscribe, unsubscribe } from "pubsub-js";
import { EXECUTE_ACTION_TOPIC, ExecuteActionMsg } from "../../common/models/pub-sub/PubSubTopics";
import { BaseActionEnum } from "../../components/action-menu/ActionMenuItem";
import { useLocation } from "react-router";

export type UIUserFavoritesContext = {
    viewID?: UIViewEnum;
    configurationID?: number;
    configurationName?: string;
};

export type UserFavoritesContextProviderProps = {
    children: ReactNode;
};

export type UseUserFavoritesContextType = [
    (context: UIUserFavoritesContext) => void, 
    () => Promise<void>,
    UIUserFavoritesContext | undefined
];

export const UserFavoritesContext = createContext<UseUserFavoritesContextType>([
    (_context: UIUserFavoritesContext) => { }, 
    async () => { }, 
    undefined
]);

export function UserFavoritesContextProvider({ children }: UserFavoritesContextProviderProps) {

    const [favoriteContext, setFavoriteContext] = useState<UIUserFavoritesContext>();
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const { acknowledge } = useConfirmation();
    const { t } = useTranslation();

    const location = useLocation();

    const { data: headerAndActionsV2Feature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.HeaderAndActionsV2,
        enabled: userState.authenticated
    });

    const headerAndActionsV2 = (headerAndActionsV2Feature ?? false);


    //console.log(`location: ${location.pathname}`)


    const { data: breadcrumb } = useQueryNotifications(useBreadcrumb)({
        customerID: userState.customer?.id as number,
        viewID: routeContext?.viewID ?? UIViewEnum.Group_SummaryListing_Devices,
        routeContext: routeContext,
        offlineDataLoaded: offlineState.offlineDataLoaded,
        offline: offlineState.offline,
        enabled: userState.authenticated
    });

    const { mutate: saveUserFavorite } = useQueryNotifications(useSaveUserFavorite)({
        onSuccess: (data) => {
            const { viewDescription, objectDescription } = splitFavoriteDescription(data.savedDescription);

            acknowledge(
                <Stack direction="column">
                    <Box>
                        {viewDescription}
                    </Box>
                    <Box>
                        {objectDescription}
                    </Box>
                    <Box sx={{ marginTop: 1 }}>
                        {t("userFavorites.savedAcknowledgement")}
                    </Box>
                </Stack>
            );
        }
    });

    // This effect should create a new favorite context every time the
    // route context changes. e.g. we navigate
    // useEffect(() => {

    //     const newContext: UIUserFavoritesContext = {
    //         viewID: routeContext?.viewID,
    //         groupID: routeContext?.groupID,
    //         deviceID: routeContext?.deviceID,
    //         tagID: routeContext?.tagID,
    //         tankID: routeContext?.tankID,
    //         wellID: routeContext?.wellID,
    //         tankBatteryID: routeContext?.tankBatteryID,
    //         configurationID: routeContext?.state?.configurationID,
    //         configurationName: undefined,
    //         organizationID: routeContext?.organizationID,
    //         token: routeContext?.token as string | undefined
    //     };

    //     console.log(`#### Creating a new favorite context => ${JSON.stringify(newContext)}`);
    //     setFavoriteContext(newContext);
    // }, [
    //     // routeContext?.viewID,
    //     // routeContext?.deviceID,
    //     // routeContext?.groupID,
    //     // routeContext?.organizationID,
    //     // routeContext?.tagID,
    //     // routeContext?.tankBatteryID,
    //     // routeContext?.tankID,
    //     // routeContext?.token,
    //     // routeContext?.wellID,
    //     // routeContext?.state?.configurationID,
    //     routeContext,
    //     setFavoriteContext
    // ]);

    const updateContext = useCallback((context: UIUserFavoritesContext) => {
        // console.log(`#### Update the favorite context => ${JSON.stringify(context)}`);

        setFavoriteContext({
            viewID: routeContext.viewID,
            ...context
        });
    }, [
        routeContext.viewID,
        setFavoriteContext
    ]);

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    const createFavorite = useCallback((): IUIUserFavoriteModel | undefined => {
        //if (favoriteContext && routeContext) {
        if (routeContext) {
            const currentRoute = routesByViewID.get(routeContext?.viewID ?? UIViewEnum.Group_SummaryListing_Devices);

            if (currentRoute) {
                const eLynxObject: IeLynxObject = {
                    id: 0,
                    objectType: eLynxObjectTypeEnum.None
                };

                switch (currentRoute.category) {
                    case UIViewCategory.Group:
                        // Site Listing / Site Details have been deprecated
                        // if (currentRoute.route.indexOf(StateNames.SiteDetails) > -1) {
                        //     eLynxObject.objectType = eLynxObjectTypeEnum.Facility;
                        //     eLynxObject.id = Number(routeContext.siteID);

                        // } else {
                        eLynxObject.objectType = eLynxObjectTypeEnum.Group;
                        eLynxObject.id = Number(routeContext.groupID);

                        // }
                        break;

                    case UIViewCategory.Device:
                        eLynxObject.objectType = eLynxObjectTypeEnum.Device;
                        eLynxObject.id = Number(routeContext.deviceID);
                        break;

                    case UIViewCategory.Tank:
                        eLynxObject.objectType = eLynxObjectTypeEnum.Tank;
                        eLynxObject.id = Number(routeContext.tankID);
                        break;

                    case UIViewCategory.Tag:
                        eLynxObject.objectType = eLynxObjectTypeEnum.Tag;
                        eLynxObject.id = Number(routeContext.tagID);
                        break;

                    case UIViewCategory.Well:
                        eLynxObject.objectType = eLynxObjectTypeEnum.Well;
                        eLynxObject.id = Number(routeContext.wellID);
                        break;

                    case UIViewCategory.TankBattery:
                        eLynxObject.objectType = eLynxObjectTypeEnum.TankBattery;
                        eLynxObject.id = Number(routeContext.tankBatteryID);
                        break;
                    //    case "admin.alarms-and-notifications":
                    //         eLynxObject.objectType = eLynxObjectTypeEnum.Group;
                    //         eLynxObject.id = Number(routeContext.groupID)
                    //         break;
                }

                const userFavoriteRouteDetails: IUIUserFavoriteRouteDetailModel = {
                    uiView: routeContext.viewID ?? UIViewEnum.None,
                    objectType: eLynxObject.objectType,
                    objectID: eLynxObject.id
                };


                const queryString = new Array<string>();

                if ((routeContext.organizationID !== undefined) &&
                    (routeContext.organizationID !== null)) {

                    userFavoriteRouteDetails.organizationID = Number(routeContext.organizationID);
                    queryString.push(`organizationID=${userFavoriteRouteDetails.organizationID}`);

                }

                if ((routeContext.groupID !== undefined) &&
                    (routeContext.groupID !== null)) {

                    userFavoriteRouteDetails.groupID = Number(routeContext.groupID);
                    queryString.push(`groupID=${userFavoriteRouteDetails.groupID}`);

                }

                // if ((routeContext.siteID !== undefined) &&
                //     (routeContext.siteID !== null)) {

                //     userFavoriteRouteDetails.siteID = Number(routeContext.siteID);
                //     queryString.push(`siteID=${userFavoriteRouteDetails.siteID}`);

                // }

                if ((routeContext.deviceID !== undefined)
                    && (routeContext.deviceID !== null)) {

                    userFavoriteRouteDetails.deviceID = Number(routeContext.deviceID);
                    queryString.push(`deviceID=${userFavoriteRouteDetails.deviceID}`);

                }

                if ((routeContext.tankID !== undefined) &&
                    (routeContext.tankID !== null)) {

                    userFavoriteRouteDetails.tankID = Number(routeContext.tankID);
                    queryString.push(`tankID=${userFavoriteRouteDetails.tankID}`);

                }

                if ((routeContext.wellID !== undefined) &&
                    (routeContext.wellID !== null)) {

                    userFavoriteRouteDetails.wellID = Number(routeContext.wellID);
                    queryString.push(`wellID=${userFavoriteRouteDetails.wellID}`);

                }

                if ((routeContext.tagID !== undefined) &&
                    (routeContext.tagID !== null)) {

                    userFavoriteRouteDetails.tagID = Number(routeContext.tagID);
                    queryString.push(`tagID=${userFavoriteRouteDetails.tagID}`);

                }


                if ((routeContext.tankBatteryID !== undefined) &&
                    (routeContext.tankBatteryID !== null)) {

                    userFavoriteRouteDetails.tankBatteryID = Number(routeContext.tankBatteryID);
                    queryString.push(`tankBatteryID=${userFavoriteRouteDetails.tankBatteryID}`);

                }


                let searchTitle = "";

                if ((routeContext.token !== undefined) &&
                    (routeContext.token !== null) &&
                    (routeContext.token.trim().length > 0)) {

                    userFavoriteRouteDetails.searchCriteria = routeContext.token.trim();
                    searchTitle = ` for ${userFavoriteRouteDetails.searchCriteria}`;
                    queryString.push(`token=${encodeURIComponent(userFavoriteRouteDetails.searchCriteria)}`);
                }

                let configurationName = "";

                // If the current favorite context belongs to this view, then add it's values to our favorite
                //if (favoriteContext.viewID === routeContext.viewID) {

                    if (favoriteContext?.configurationID !== undefined &&
                        favoriteContext.configurationID !== null) {
    
                        userFavoriteRouteDetails.configurationID = Number(favoriteContext.configurationID);
                        queryString.push(`configurationID=${userFavoriteRouteDetails.configurationID}`);

                        configurationName = favoriteContext.configurationName ? ` - ${favoriteContext.configurationName}` : "";
                    }
                //}

                const path = breadcrumb?.breadcrumbItems.map(item => item.title).join("/");
                const title = currentRoute.name;

                const newFavorite: IUIUserFavoriteModel = {
                    id: -1,
                    userID: -1,
                    url: "",
                    redirect: `${currentRoute.path}?${queryString.join("&")}`,
                    description: `${title}${searchTitle} (${path})${configurationName}`,
                    order: -1,
                    isHomeUrl: false,
                    object: eLynxObject,
                    routeDetail: userFavoriteRouteDetails,
                    eLynxAppID: eLynxAppIDEnum.eLynxApp2022
                };

                return newFavorite;
            }
        }
    }, [
        breadcrumb?.breadcrumbItems,
        favoriteContext,
        routeContext
    ]);
    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


    // //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // const createFavorite = useCallback((): IUIUserFavoriteModel | undefined => {
    //     if (favoriteContext) {
    //         const currentRoute = routesByViewID.get(favoriteContext?.viewID ?? UIViewEnum.Group_SummaryListing_Devices);

    //         if (currentRoute) {
    //             let eLynxObject: IeLynxObject = {
    //                 id: 0,
    //                 objectType: eLynxObjectTypeEnum.None
    //             };

    //             switch (currentRoute.category) {
    //                 case UIViewCategory.Group:
    //                     // Site Listing / Site Details have been deprecated
    //                     // if (currentRoute.route.indexOf(StateNames.SiteDetails) > -1) {
    //                     //     eLynxObject.objectType = eLynxObjectTypeEnum.Facility;
    //                     //     eLynxObject.id = Number(favoriteContext.siteID);

    //                     // } else {
    //                     eLynxObject.objectType = eLynxObjectTypeEnum.Group;
    //                     eLynxObject.id = Number(favoriteContext.groupID);

    //                     // }
    //                     break;

    //                 case UIViewCategory.Device:
    //                     eLynxObject.objectType = eLynxObjectTypeEnum.Device;
    //                     eLynxObject.id = Number(favoriteContext.deviceID);
    //                     break;

    //                 case UIViewCategory.Tank:
    //                     eLynxObject.objectType = eLynxObjectTypeEnum.Tank;
    //                     eLynxObject.id = Number(favoriteContext.tankID);
    //                     break;

    //                 case UIViewCategory.Tag:
    //                     eLynxObject.objectType = eLynxObjectTypeEnum.Tag;
    //                     eLynxObject.id = Number(favoriteContext.tagID);
    //                     break;

    //                 case UIViewCategory.Well:
    //                     eLynxObject.objectType = eLynxObjectTypeEnum.Well;
    //                     eLynxObject.id = Number(favoriteContext.wellID);
    //                     break;

    //                 case UIViewCategory.TankBattery:
    //                     eLynxObject.objectType = eLynxObjectTypeEnum.TankBattery;
    //                     eLynxObject.id = Number(favoriteContext.tankBatteryID);
    //                     break;
    //                 //    case "admin.alarms-and-notifications":
    //                 //         eLynxObject.objectType = eLynxObjectTypeEnum.Group;
    //                 //         eLynxObject.id = Number(favoriteContext.groupID)
    //                 //         break;
    //             }

    //             const userFavoriteRouteDetails: IUIUserFavoriteRouteDetailModel = {
    //                 uiView: favoriteContext.viewID ?? UIViewEnum.None,
    //                 objectType: eLynxObject.objectType,
    //                 objectID: eLynxObject.id
    //             };


    //             const queryString = new Array<any>();

    //             if ((favoriteContext.organizationID !== undefined) &&
    //                 (favoriteContext.organizationID !== null)) {

    //                 userFavoriteRouteDetails.organizationID = Number(favoriteContext.organizationID);
    //                 queryString.push(`organizationID=${userFavoriteRouteDetails.organizationID}`);

    //             }

    //             if ((favoriteContext.groupID !== undefined) &&
    //                 (favoriteContext.groupID !== null)) {

    //                 userFavoriteRouteDetails.groupID = Number(favoriteContext.groupID);
    //                 queryString.push(`groupID=${userFavoriteRouteDetails.groupID}`);

    //             }

    //             // if ((favoriteContext.siteID !== undefined) &&
    //             //     (favoriteContext.siteID !== null)) {

    //             //     userFavoriteRouteDetails.siteID = Number(favoriteContext.siteID);
    //             //     queryString.push(`siteID=${userFavoriteRouteDetails.siteID}`);

    //             // }

    //             if ((favoriteContext.deviceID !== undefined)
    //                 && (favoriteContext.deviceID !== null)) {

    //                 userFavoriteRouteDetails.deviceID = Number(favoriteContext.deviceID);
    //                 queryString.push(`deviceID=${userFavoriteRouteDetails.deviceID}`);

    //             }

    //             if ((favoriteContext.tankID !== undefined) &&
    //                 (favoriteContext.tankID !== null)) {

    //                 userFavoriteRouteDetails.tankID = Number(favoriteContext.tankID);
    //                 queryString.push(`tankID=${userFavoriteRouteDetails.tankID}`);

    //             }

    //             if ((favoriteContext.wellID !== undefined) &&
    //                 (favoriteContext.wellID !== null)) {

    //                 userFavoriteRouteDetails.wellID = Number(favoriteContext.wellID);
    //                 queryString.push(`wellID=${userFavoriteRouteDetails.wellID}`);

    //             }

    //             if ((favoriteContext.tagID !== undefined) &&
    //                 (favoriteContext.tagID !== null)) {

    //                 userFavoriteRouteDetails.tagID = Number(favoriteContext.tagID);
    //                 queryString.push(`tagID=${userFavoriteRouteDetails.tagID}`);

    //             }

    //             if ((favoriteContext.configurationID !== undefined) &&
    //                 // (favoriteContext.configurationID !== null) && (currentRoute.category !== "admin.alarms-and-notifications")) {
    //                 (favoriteContext.configurationID !== null)) {

    //                 userFavoriteRouteDetails.configurationID = Number(favoriteContext.configurationID);
    //                 queryString.push(`configurationID=${userFavoriteRouteDetails.configurationID}`);

    //             }

    //             let searchTitle = "";

    //             if ((favoriteContext.token !== undefined) &&
    //                 (favoriteContext.token !== null) &&
    //                 (favoriteContext.token.trim().length > 0)) {

    //                 userFavoriteRouteDetails.searchCriteria = favoriteContext.token.trim();
    //                 searchTitle = ` for ${userFavoriteRouteDetails.searchCriteria}`;
    //                 queryString.push(`token=${encodeURIComponent(userFavoriteRouteDetails.searchCriteria)}`);
    //             }

    //             const path = breadcrumb?.breadcrumbItems.map(item => item.title).join("/");
    //             const title = currentRoute.name;
    //             const configurationName = favoriteContext.configurationName ? ` - ${favoriteContext.configurationName}` : "";

    //             var newFavorite: IUIUserFavoriteModel = {
    //                 id: -1,
    //                 userID: -1,
    //                 url: "",
    //                 redirect: `${currentRoute.path}?${queryString.join("&")}`,
    //                 description: `${title}${searchTitle} (${path})${configurationName}`,
    //                 order: -1,
    //                 isHomeUrl: false,
    //                 object: eLynxObject,
    //                 routeDetail: userFavoriteRouteDetails,
    //                 eLynxAppID: eLynxAppIDEnum.eLynxApp2022
    //             };

    //             return newFavorite;
    //         }
    //     }
    // }, [
    //     breadcrumb?.breadcrumbItems,
    //     favoriteContext
    // ]);
    // //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    const saveFavorite = useCallback(async (): Promise<void> => {
        const favorite = createFavorite();

        //console.log(`#### Saving a favorite => ${JSON.stringify(favorite)}`);

        if (favorite) {
            return saveUserFavorite(favorite);
        }
    }, [
        createFavorite,
        saveUserFavorite
    ]);



        useEffect(() => {
    
            if (headerAndActionsV2) {

                const token = subscribe(EXECUTE_ACTION_TOPIC, (message, data) => {
                    const actionData = data as ExecuteActionMsg;
                    if (actionData.action === BaseActionEnum.Favorite) {
                        saveFavorite();
                    }
                });
        
                return () => {
                    if (token) {
                        unsubscribe(token);
                    }
                }
            }
    
        }, [
            headerAndActionsV2,
            saveFavorite
        ]);
    
    return (
        <UserFavoritesContext.Provider value={[
            updateContext,
            saveFavorite,
            favoriteContext
        ]}>
            {children}
        </UserFavoritesContext.Provider>
    );
}

export function useUserFavoritesContext() {
    const context = React.useContext<UseUserFavoritesContextType>(UserFavoritesContext);
    if (!context) {
        throw new Error("useUserFavoritesContext must be used within a UserFavoritesContextProvider");
    }
    return context;
}